import { type AxiosError, type AxiosResponse } from 'axios'
import { useRef } from 'react'
import useSWRMutation, { type SWRMutationResponse } from 'swr/mutation'

import { type Authentication400 } from '@redteclab/api/clients/bully'

import { buildHomeOneApiAxiosInstance } from '../../../api'
import {
  type PublicGlobalConfig,
  useGlobalConfigContext,
} from '../../../global-config'
import { gtmDataLayerPushRegistration } from '../../../gtm'
import { urlResolverGetUserRegisterApiEndpoint } from '../../../url-handling'
import {
  ERR_MESSAGES_FIELD_MAP_REGISTRATION,
  type RegistrationType,
} from '../../consts.ts'
import { getRegistrationApiInvalidParamsErrorKeys } from '../../registrationApiError.ts'
import { resolveRegistrationType } from '../../registrationTracking'
import { type HomeOneRegistrationRequest } from '../homeOneRegistrationRequest'

import { refetchCustomerBasicInformationWhenUserLogsIn } from './useUserApiLoginMutation'

const userRegistrationApiFetcher =
  (globalConfig: PublicGlobalConfig) =>
  async (
    url: string,
    data: { arg: HomeOneRegistrationRequest },
  ): Promise<AxiosResponse> => {
    return buildHomeOneApiAxiosInstance(globalConfig).post(url, data.arg)
  }

const GTM_REGISTRATION_ERROR_CODE_GENERAL = 2
const GTM_REGISTRATION_ERROR_CODE_EMAIL_ERROR = 1.1
const GTM_REGISTRATION_ERROR_CODE_FIRST_NAME = 1.2
const GTM_REGISTRATION_ERROR_CODE_LAST_NAME = 1.3
const GTM_REGISTRATION_ERROR_CODE_DOB = 1.4
const GTM_REGISTRATION_ERROR_CODE_PASSWORD = 1.5
const GTM_REGISTRATION_ERROR_CODE_SALUTATION = 1.6

const getEventCodeForRegistrationError = (
  invalidParamKey: string | undefined,
): number => {
  switch (invalidParamKey) {
    case 'birthDaySelectedYear': {
      return GTM_REGISTRATION_ERROR_CODE_DOB
    }
    case 'email': {
      return GTM_REGISTRATION_ERROR_CODE_EMAIL_ERROR
    }
    case 'firstName': {
      return GTM_REGISTRATION_ERROR_CODE_FIRST_NAME
    }
    case 'lastName': {
      return GTM_REGISTRATION_ERROR_CODE_LAST_NAME
    }
    case 'password': {
      return GTM_REGISTRATION_ERROR_CODE_PASSWORD
    }
    case 'salutation': {
      return GTM_REGISTRATION_ERROR_CODE_SALUTATION
    }
    default: {
      return GTM_REGISTRATION_ERROR_CODE_GENERAL
    }
  }
}

const pushRegistrationWithPasswordGtmErrorEvent = (
  error: AxiosError<Authentication400>,
): void => {
  const invalidParamsErrorKeys = getRegistrationApiInvalidParamsErrorKeys(error)
  for (const invalidParamKey of invalidParamsErrorKeys) {
    const fieldName = ERR_MESSAGES_FIELD_MAP_REGISTRATION.get(invalidParamKey)
    gtmDataLayerPushRegistration(getEventCodeForRegistrationError(fieldName))
  }
}

export const useUserRegistrationMutation = ({
  onError,
  onSuccess,
}: {
  onError: (err: AxiosError<Authentication400>) => void
  onSuccess: (afterLoginSideEffectsPromise: Promise<void>) => void
}): SWRMutationResponse<
  AxiosResponse,
  AxiosError<Authentication400>,
  string,
  HomeOneRegistrationRequest
> => {
  const globalConfig = useGlobalConfigContext()
  const registrationTypeRef = useRef<RegistrationType>('password')

  const registrationFetcher = async (
    url: string,
    data: { arg: HomeOneRegistrationRequest },
  ): Promise<AxiosResponse> => {
    registrationTypeRef.current = resolveRegistrationType(data.arg)

    return userRegistrationApiFetcher(globalConfig)(url, data)
  }

  return useSWRMutation(
    urlResolverGetUserRegisterApiEndpoint(),
    registrationFetcher,
    {
      onError: (error: AxiosError<Authentication400>) => {
        pushRegistrationWithPasswordGtmErrorEvent(error)
        onError(error)
      },
      onSuccess: () => {
        onSuccess(
          refetchCustomerBasicInformationWhenUserLogsIn(
            globalConfig,
            'registration',
            registrationTypeRef.current,
          ),
        )
      },
    },
  )
}
